// MetricsDashboard — internal metrics-surface composition primitive
// (CHG-1307 / ITER-3434).
//
// A page-level layout for platform-internal metric reports. Renders:
//   • PageHeader (consistent chrome)
//   • Stat strip (compact v1 vs v2 totals per brand)
//   • Time-series chart (svg, dense daily series — no external charting
//     dep; CSS-styled bars sized by relative max value)
//   • Funnel (horizontal stage progression with computed conversion rates)
//
// Purely presentational — the page (V2AdoptionDashboard.tsx) owns the
// data fetch + loading + error state. This primitive accepts already-
// shaped data and renders chrome. No theme colors hardcoded; everything
// uses --ag-* / --color-* CSS vars per Rule 5.
//
// Per .forge/plans/v2-canary-pages.md and the dashboard-v2 composition
// pattern (CHG-1238 DashboardPage precedent).

import type { ReactNode } from "react";
import { PageHeader, type PageHeaderAction } from "./PageHeader";

export interface StatStripBrandRow {
  brand_id: string;
  v1: number;
  v2: number;
}

export interface StatStripData {
  window_days: number;
  v1_total: number;
  v2_total: number;
  by_brand: StatStripBrandRow[];
}

export interface TimeSeriesPoint {
  date: string;
  v1: number;
  v2: number;
}

export interface TimeSeriesData {
  window_days: number;
  daily: TimeSeriesPoint[];
}

export interface FunnelStep {
  label: string;
  event_name: string;
  count: number;
}

export interface FunnelData {
  window_days: number;
  steps: FunnelStep[];
}

export interface MetricsDashboardProps {
  title: string;
  subtitle?: string;
  headerActions?: PageHeaderAction[];
  statStrip: StatStripData;
  timeSeries: TimeSeriesData;
  funnel: FunnelData;
  /** Optional caption rendered above the stat strip (e.g. "Filtered to org X"). */
  filterCaption?: ReactNode;
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function MetricsDashboard({
  title,
  subtitle,
  headerActions,
  statStrip,
  timeSeries,
  funnel,
  filterCaption,
  loading,
  error,
  onRetry,
}: MetricsDashboardProps) {
  return (
    <main
      role="main"
      style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 48px" }}
    >
      <PageHeader title={title} subtitle={subtitle} actions={headerActions} />

      {error && !loading ? (
        <ErrorBanner error={error} onRetry={onRetry} />
      ) : null}

      {filterCaption ? (
        <div
          data-metrics-filter-caption
          style={{
            margin: "0 0 12px",
            fontSize: 12,
            color: "var(--color-text-muted)",
          }}
        >
          {filterCaption}
        </div>
      ) : null}

      <Section title={`Active sessions · trailing ${statStrip.window_days} days`}>
        <StatStrip data={statStrip} />
      </Section>

      <Section title={`Daily sessions · trailing ${timeSeries.window_days} days`}>
        <TimeSeriesChart data={timeSeries} />
      </Section>

      <Section title={`Adoption funnel · trailing ${funnel.window_days} days`}>
        <Funnel data={funnel} />
      </Section>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────
// Section wrapper
// ─────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h2
        className="font-display"
        style={{
          margin: "0 0 10px",
          fontSize: 14,
          fontWeight: 600,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {title}
      </h2>
      <div
        className="card"
        style={{
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md)",
          padding: 16,
          background: "var(--color-surface)",
        }}
      >
        {children}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Stat strip
// ─────────────────────────────────────────────────────────────────

function StatStrip({ data }: { data: StatStripData }) {
  const totalAll = data.v1_total + data.v2_total;
  const v2Share = totalAll === 0 ? 0 : Math.round((data.v2_total / totalAll) * 100);
  return (
    <div data-metrics-stat-strip>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCell label="v1 sessions" value={data.v1_total} tone="muted" />
        <StatCell label="v2 sessions" value={data.v2_total} tone="primary" />
        <StatCell label="v2 share" value={`${v2Share}%`} tone="primary" />
      </div>
      {data.by_brand.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          No brand-level breakdown for this window.
        </div>
      ) : (
        <table
          data-metrics-brand-table
          style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}
        >
          <thead>
            <tr style={{ textAlign: "left", color: "var(--color-text-muted)" }}>
              <th style={{ padding: "6px 8px", fontWeight: 500 }}>Brand</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>v1</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>v2</th>
              <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>v2 share</th>
            </tr>
          </thead>
          <tbody>
            {data.by_brand.map((row) => {
              const rowTotal = row.v1 + row.v2;
              const rowShare = rowTotal === 0 ? 0 : Math.round((row.v2 / rowTotal) * 100);
              return (
                <tr
                  key={row.brand_id}
                  data-brand-id={row.brand_id}
                  style={{ borderTop: "1px solid var(--color-border-subtle)" }}
                >
                  <td style={{ padding: "6px 8px" }}>{row.brand_id}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>{row.v1}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>{row.v2}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>{rowShare}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatCell({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "primary" | "muted";
}) {
  return (
    <div
      style={{
        padding: 12,
        background: "var(--color-bg-subtle)",
        borderRadius: "var(--radius-sm)",
      }}
    >
      <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: tone === "primary" ? "var(--color-primary)" : "var(--color-text)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Time-series chart
// ─────────────────────────────────────────────────────────────────

function TimeSeriesChart({ data }: { data: TimeSeriesData }) {
  const max = Math.max(1, ...data.daily.flatMap((d) => [d.v1, d.v2]));
  return (
    <div data-metrics-time-series>
      <div
        style={{
          display: "flex",
          gap: 2,
          alignItems: "flex-end",
          height: 120,
          marginBottom: 8,
        }}
      >
        {data.daily.map((d) => (
          <div
            key={d.date}
            data-day={d.date}
            data-v1={d.v1}
            data-v2={d.v2}
            title={`${d.date}\nv1: ${d.v1}  v2: ${d.v2}`}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <div
              style={{
                height: `${(d.v2 / max) * 80}px`,
                background: "var(--color-primary)",
                minHeight: d.v2 > 0 ? 2 : 0,
                borderRadius: "1px 1px 0 0",
              }}
            />
            <div
              style={{
                height: `${(d.v1 / max) * 80}px`,
                background: "var(--color-text-muted)",
                minHeight: d.v1 > 0 ? 2 : 0,
                opacity: 0.55,
              }}
            />
          </div>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: "var(--color-text-muted)",
        }}
      >
        <span>{data.daily[0]?.date ?? "—"}</span>
        <span>{data.daily[data.daily.length - 1]?.date ?? "—"}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 16,
          marginTop: 8,
          fontSize: 11,
          color: "var(--color-text-muted)",
        }}
      >
        <Swatch color="var(--color-primary)" label="v2" />
        <Swatch color="var(--color-text-muted)" label="v1" />
      </div>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          background: color,
          borderRadius: 2,
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// Funnel
// ─────────────────────────────────────────────────────────────────

function Funnel({ data }: { data: FunnelData }) {
  const top = data.steps[0]?.count ?? 0;
  return (
    <div data-metrics-funnel>
      {data.steps.map((step, i) => {
        const stepRate = top === 0 ? 0 : Math.round((step.count / top) * 100);
        const widthPct = top === 0 ? 0 : Math.max(8, (step.count / top) * 100);
        return (
          <div
            key={step.label}
            data-funnel-step={step.label}
            data-funnel-count={step.count}
            style={{
              marginBottom: i === data.steps.length - 1 ? 0 : 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "var(--color-text-muted)",
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 500, color: "var(--color-text)" }}>
                {humanizeFunnelLabel(step.label)}
              </span>
              <span>
                {step.count} ({stepRate}% of top)
              </span>
            </div>
            <div
              style={{
                height: 12,
                width: `${widthPct}%`,
                background: "var(--color-primary)",
                opacity: 0.85 - i * 0.18,
                borderRadius: 3,
                minWidth: step.count > 0 ? 6 : 0,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

const FUNNEL_LABELS: Record<string, string> = {
  banner_shown: "v1 banner shown",
  banner_clicked: "v1 banner clicked",
  v2_session_started: "v2 session started",
  welcome_completed: "v2 welcome completed",
};

function humanizeFunnelLabel(label: string): string {
  return FUNNEL_LABELS[label] ?? label;
}

// ─────────────────────────────────────────────────────────────────
// Error banner
// ─────────────────────────────────────────────────────────────────

function ErrorBanner({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      style={{
        padding: 12,
        marginBottom: 16,
        background: "var(--color-error-tint)",
        border: "1px solid var(--color-error)",
        borderRadius: "var(--radius-md)",
        display: "flex",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ flex: 1, fontSize: 13, color: "var(--color-text)" }}>
        <div style={{ fontWeight: 500 }}>Couldn’t load adoption metrics</div>
        <div style={{ marginTop: 2, color: "var(--color-text-muted)" }}>
          {error.message || "Unknown error"}
        </div>
      </div>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}
